import React from 'react';
import { Link } from 'react-router-dom';
import { ChefHat, Leaf, BookOpen, Heart, Star, Users, UtensilsCrossed, CalendarCheck } from 'lucide-react';
import { restaurantInfo, signatureDishes, testimonials, whyChooseUs } from '../mock';

const Home = () => {
  const features = [
    { icon: <ChefHat className="w-8 h-8" />, title: `${restaurantInfo.yearsEstablished}+ Years of Culinary Excellence`, description: "Consistently delivering exceptional dining experiences" },
    { icon: <Users className="w-8 h-8" />, title: "Private Party Hall Available", description: "Perfect venue for your special celebrations" },
    { icon: <Leaf className="w-8 h-8" />, title: "Fresh Ingredients Daily", description: "Quality and freshness in every dish" },
    { icon: <UtensilsCrossed className="w-8 h-8" />, title: "Dine-in & Takeout", description: "Enjoy our cuisine your way" }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[600px] md:h-[700px] flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1600')" }}>
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 animate-fade-in" style={{ fontFamily: "'Playfair Display', serif" }}>
            {restaurantInfo.tagline}
          </h1>
          <p className="text-xl md:text-2xl mb-2 text-gray-200">
            {restaurantInfo.description}
          </p>
          <p className="text-lg md:text-xl mb-8 text-gray-300">
            Savor our award-winning North Indian, Chinese & Continental cuisine. Rated {restaurantInfo.rating}★ by {restaurantInfo.reviewCount}+ happy diners
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary text-center">
              Reserve Your Table
            </Link>
            <a href={`tel:${restaurantInfo.phone}`} className="btn-secondary text-center bg-white/10 border-white text-white hover:bg-white hover:text-[#2C3E50]">
              Call Now
            </a>
          </div>
          <div className="mt-6">
            <a href={`tel:${restaurantInfo.phone}`} className="text-xl font-semibold text-[#F39C12] hover:text-[#e67e22]">
              {restaurantInfo.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Featured Highlights */}
      <section className="section-padding bg-[#FFF8F0]">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 text-center">
                <div className="flex justify-center mb-4 text-[#E84118]">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#2C3E50]">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Signature Dishes */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif", color: '#2C3E50' }}>
              Our Signature Dishes
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Indulge in our chef's specialties, crafted with passion and perfected over years
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {signatureDishes.slice(0, 8).map((dish) => (
              <div key={dish.id} className="bg-white rounded-lg shadow-md overflow-hidden hover-lift">
                <div className="h-48 bg-cover bg-center" style={{ backgroundImage: `url('${dish.image}')` }}></div>
                <div className="p-4">
                  {dish.isSignature && (
                    <span className="inline-block px-3 py-1 text-xs font-semibold text-white bg-[#E84118] rounded-full mb-2">
                      Chef's Special
                    </span>
                  )}
                  <h3 className="text-lg font-semibold mb-2 text-[#2C3E50]">{dish.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{dish.description}</p>
                  <p className="text-[#F39C12] font-bold">{dish.price}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/menu" className="btn-primary">
              View Full Menu
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-[#FFF8F0]">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif", color: '#2C3E50' }}>
              Why Choose The Grill
            </h2>
            <p className="text-gray-600 text-lg">
              Experience the difference that passion and dedication make
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E84118] flex items-center justify-center text-white">
                  {item.icon === 'ChefHat' && <ChefHat className="w-8 h-8" />}
                  {item.icon === 'Leaf' && <Leaf className="w-8 h-8" />}
                  {item.icon === 'BookOpen' && <BookOpen className="w-8 h-8" />}
                  {item.icon === 'Heart' && <Heart className="w-8 h-8" />}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-[#2C3E50]">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif", color: '#2C3E50' }}>
              What Our Customers Say
            </h2>
            <div className="flex items-center justify-center space-x-2 text-[#F39C12] text-xl">
              <Star className="w-6 h-6 fill-current" />
              <span className="font-bold">{restaurantInfo.rating}</span>
              <span className="text-gray-600">({restaurantInfo.reviewCount}+ Reviews)</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((testimonial) => (
              <div key={testimonial.id} className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#F39C12] fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-[#2C3E50]">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.date}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/testimonials" className="btn-secondary">
              Read All Reviews
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#E84118] text-white">
        <div className="container text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ready to Experience The Grill?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Book your table today and discover why families across Jabalpur have made us their favorite dining destination
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-secondary bg-white text-[#E84118] border-white hover:bg-gray-100">
              Reserve Now
            </Link>
            <Link to="/events" className="btn-gold">
              Plan Your Event
            </Link>
          </div>
        </div>
      </section>

      {/* Statistics Bar */}
      <section className="bg-[#2C3E50] text-white py-8">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-[#F39C12] mb-1">{restaurantInfo.rating}★</div>
              <div className="text-sm text-gray-300">Google Rating</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#F39C12] mb-1">{restaurantInfo.reviewCount}+</div>
              <div className="text-sm text-gray-300">Happy Reviews</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#F39C12] mb-1">{restaurantInfo.yearsEstablished}+</div>
              <div className="text-sm text-gray-300">Years Serving</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#F39C12] mb-1">{restaurantInfo.menuItemCount}+</div>
              <div className="text-sm text-gray-300">Menu Items</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
